import { captureMessage } from '@sentry/core'

const originalFetchRaw = $fetch.raw

// application/json and the +json structured syntaxes (application/ld+json, application/problem+json)
const JSON_MEDIA_TYPE = /^application\/(?:[\w!#$%&*.^`~-]*\+)?json$/i

/**
 * The API sends a single `content-type: application/json`, but its answer passes the Vercel hop and
 * the visitor's own proxy or in-app browser, some of which add a second Content-Type header;
 * Headers.get() joins repeated headers with ", " and a charset parameter follows ";"
 */
const isJsonContentType = (contentType: string): boolean => {
  const mediaTypes = contentType
    .split(',')
    .map(type => type.split(';')[0].trim().toLowerCase())
    .filter(Boolean)

  return mediaTypes.length > 0 && mediaTypes.every(mediaType => JSON_MEDIA_TYPE.test(mediaType))
}

export default defineNuxtPlugin((nuxtApp) => {
  const { $config } = nuxtApp
  const { API_BASE_PATH } = $config.public

  /**
   * Sentry report when the API answers 2xx with something other than JSON. A 4xx/5xx (a gateway or
   * firewall page) is thrown by ofetch before this runs and is classified by the service catches.
   */
  $fetch.raw = (async (request, options) => {
    const response = await originalFetchRaw(request, options)

    const isApi = String(request).includes(API_BASE_PATH)
    if (isApi) {
      const contentType = response.headers.get('content-type') || ''

      // ofetch leaves the body text in _data when it does not parse as JSON
      if (!isJsonContentType(contentType) || typeof response._data === 'string') {
        captureMessage(`FATAL: Non-JSON response from ${request}`, {
          level: 'fatal',
          extra: {
            url: request,
            request: options,
            response,
            status: response.status,
            statusText: response.statusText,
            contentType,
          },
        })
      }
    }

    return response
  }) as typeof originalFetchRaw
})
